/**
 * Клиентская карточка рабочего места (роут `/workplaces/:id`, p2-ot-section §9.5): загрузка по
 * id + индикатор/ошибка + кнопка «Назад» (по образцу `pages/employee.tsx`/`employee-card-loader.tsx`).
 * Возврат «Назад» (D1, p3-structure-sout-training §2): `useBackTarget('/ot')` — на страницу, с
 * которой был выполнен переход (`location.state.from`), а без неё — фолбэк «Охрана труда», как
 * было раньше. Текущий адрес карточки передаётся дальше в `WorkplaceCardView` (`backFrom`), чтобы
 * ссылки на связанных сотрудников несли `state.from` для их собственной кнопки «Назад» — цепочка
 * «место → сотрудник → назад» возвращает именно на карточку места.
 * `reloadToken` (p3-structure-sout-training §3, code-review волны 2): историческая СОУТ,
 * сохранённая через `HistoricalSoutPanel` в `WorkplaceCardView`, бампает токен — перезапрос
 * `getWorkplace` подтягивает свежие «Сведения по СОУТ»/«Хронология СОУТ» (по образцу
 * `onHistoricalTrainingSaved` в `employee-card-loader.tsx`).
 */
import { useEffect, useMemo, useState } from 'react'
import { ArrowLeft } from 'lucide-react'
import { useLocation, useParams } from 'react-router-dom'
import { companyApi, teamClientApi, type Department, type WorkplaceCard } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { backState, useBackTarget } from '@/lib/use-back-target'
import { PageHead } from '@/design-system/components/layout/page-head'
import { Button } from '@/design-system/components/primitives/button'
import { Alert } from '@/design-system/components/feedback/alert'
import { Card } from '@/design-system/components/layout/card'
import { Dialog, DialogContent } from '@/design-system/components/primitives/dialog'
import { WorkplaceCardView } from '@/company/workplace-card'
import { WorkplaceForm, type WorkplaceFormInitial } from '@/company/workplace-form'
import { WorkplaceDeleteDialog } from '@/company/workplace-delete-dialog'

export function WorkplacePage() {
  const location = useLocation()
  // Команда открывает место выбранного клиента (роут с :cid, centrika-docs!61) — скоуп этого
  // клиента; клиент — свой скоуп. cid в пути переживает прямую ссылку/перезагрузку/новую вкладку.
  const { id, cid } = useParams<{ id: string; cid?: string }>()
  const api = useMemo(() => (cid ? teamClientApi(cid) : companyApi), [cid])
  // «Назад» без истории: у команды — карточка клиента на вкладке «Структура», у клиента — «ОТ».
  const goBack = useBackTarget(cid ? `/team/clients/${cid}?tab=structure` : '/ot')
  const [workplace, setWorkplace] = useState<WorkplaceCard | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [reloadToken, setReloadToken] = useState(0)
  // Изменение/удаление (centrika-docs!78). departments грузятся ЛЕНИВО (при открытии формы
  // «Изменение») — карточка места не должна дёргать список подразделений на каждом заходе.
  const [editOpen, setEditOpen] = useState(false)
  const [departments, setDepartments] = useState<Department[]>([])
  const [deleting, setDeleting] = useState(false)
  // Подтверждение удаления (docs!84): кнопка «Удалить» открывает диалог, сам запрос — только после
  // подтверждения в нём.
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    api
      .getWorkplace(id, controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setWorkplace(res)
      })
      .catch((err) => {
        if (!controller.signal.aborted)
          setError(getErrorMessage(err, 'Не удалось загрузить карточку рабочего места'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [api, id, reloadToken])

  async function openEdit() {
    setActionError(null)
    try {
      const res = await api.listDepartments()
      setDepartments(res.items)
    } catch {
      // Список подразделений не загрузился — форма всё равно откроется: текущее подразделение
      // предзаполнено по имени/id из карточки, DepartmentSelect деградирует до ввода.
      setDepartments([])
    }
    setEditOpen(true)
  }

  async function handleDelete() {
    if (!id || deleting) return
    setActionError(null)
    setDeleting(true)
    try {
      await api.deleteWorkplace(id)
      // Успех: переход на страницу, куда ведёт «Назад» (страница-источник/дефолт роли).
      goBack()
    } catch (err) {
      // Ошибка (в т.ч. 409 «Нельзя удалить рабочее место с сотрудниками») — остаёмся на странице.
      // Диалог подтверждения закрываем, чтобы освободить экран под сообщение об ошибке (docs!84);
      // текст ошибки/возврат на источник не меняются.
      setActionError(getErrorMessage(err, 'Не удалось удалить рабочее место'))
      setDeleting(false)
      setConfirmDeleteOpen(false)
    }
  }

  const editInitial: WorkplaceFormInitial | null = workplace
    ? {
        department: workplace.departmentName,
        departmentId: workplace.departmentId || undefined,
        position: workplace.position,
        positionId: workplace.positionId || undefined,
        actualAddress: workplace.actualAddress,
        shiftHours: workplace.shiftHours,
        weekHours: workplace.weekHours,
        schedule: workplace.schedule,
        hasNoEmployee: workplace.hasNoEmployee,
      }
    : null

  return (
    <div>
      <PageHead title="Карточка рабочего места" />
      <div className="mb-4">
        <Button
          variant="secondary"
          size="sm"
          onClick={goBack}
          leftIcon={<ArrowLeft className="size-4 stroke-current" aria-hidden />}
        >
          Назад
        </Button>
      </div>
      {loading ? (
        <p className="py-10 text-center text-txt-secondary">Загрузка…</p>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : workplace ? (
        <>
          {actionError ? (
            <Alert variant="danger" className="mb-4">
              {actionError}
            </Alert>
          ) : null}
          <WorkplaceCardView
            workplace={workplace}
            onSoutHistoricalSaved={() => setReloadToken((t) => t + 1)}
            backFrom={backState(location).from}
            onEdit={openEdit}
            onDelete={() => {
              // Кнопка «Удалить» лишь открывает подтверждение; запрос — из onConfirm диалога.
              setActionError(null)
              setConfirmDeleteOpen(true)
            }}
            deleting={deleting}
          />
          <WorkplaceDeleteDialog
            open={confirmDeleteOpen}
            deleting={deleting}
            onConfirm={handleDelete}
            onClose={() => setConfirmDeleteOpen(false)}
          />
          {editOpen && editInitial ? (
            <Dialog
              open
              onOpenChange={(next) => {
                if (!next) setEditOpen(false)
              }}
            >
              <DialogContent title="Редактирование рабочего места" className="max-w-2xl">
                <Card header="Редактирование рабочего места">
                  <WorkplaceForm
                    api={api}
                    variant="edit"
                    departments={departments}
                    workplaceId={workplace.id}
                    initial={editInitial}
                    onSaved={() => {
                      setEditOpen(false)
                      // Обновляем основные сведения страницы (критерий приёмки 3).
                      setReloadToken((t) => t + 1)
                    }}
                    onCancel={() => setEditOpen(false)}
                  />
                </Card>
              </DialogContent>
            </Dialog>
          ) : null}
        </>
      ) : null}
    </div>
  )
}
